import { type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

import { usePropertyOptions as useBasePropertyOptions } from '../../../components/form/property-select.js';
import { useApiQuery } from '../../../lib/api/hooks.js';
import { queryKeys } from '../../../lib/api/query-keys.js';
import { userEndpoints } from './endpoints.js';
import {
  roleOption,
  userDetail,
  type PropertyOption,
  type RoleOption,
  type UserDetail,
} from './schemas.js';

/**
 * Reads for the Users module.
 *
 * The list is handled by `ResourceListPage`; only the detail read and the
 * role/property vocabularies the form needs live here.
 */

export function useUser(userId: string): UseQueryResult<UserDetail> {
  return useApiQuery({
    queryKey: queryKeys.users.detail(userId),
    path: userEndpoints.detail(userId),
    schema: userDetail,
    enabled: userId !== '',
  });
}

/** The roles that can be assigned to a user. */
export function useRoleOptions(): UseQueryResult<readonly RoleOption[]> {
  return useApiQuery({
    queryKey: queryKeys.roles.options({}),
    path: '/v1/roles',
    query: { pageSize: 100 },
    schema: z.array(roleOption),
  });
}

/** The properties a user can be scoped to. */
export function usePropertyOptions(): UseQueryResult<readonly PropertyOption[]> {
  return useBasePropertyOptions();
}
